import React from 'react';
import {
  Grid,
  Button,
  ButtonGroup,
  Slide,
  LinearProgress,
  Typography,
  Box,
} from '@mui/material';
import Footer from '../Footer/Footer';
import AutoField, {FormField, FieldValue} from '../AutoForm/AutoFieldNew';
import {Condition} from './operators';
import {getEnvironment} from '../../utils/envTest';
import {wizardFieldsType} from './Wizard';
import theme from '../../theme/Theme';

const classes = {
  questionText: {
    fontSize: '1.1rem',
    color: theme.palette.text.secondary,
  },
  progressWrapper: {
    width: '20rem',
    background: theme.palette.primary.light,
    paddingTop: '5px',
    paddingBottom: '5px',
    paddingLeft: '3px',
    paddingRight: '3px',
    transitionDuration: '0.1s',
    transitionProperty: 'background-color, color, border-color, opacity, box-shadow',
    transitionTimingFunction: 'ease-out',
    cursor: 'pointer',
    padding: '4px',
    minHeight: '40px',
    minWidth: '75px',
    maxWidth: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '4px',
  },
  progressText: {
    fontSize: '12px',
  },
  progress: {
    width: '100%',
    height: '5px',
  },
};

export type wizardQuestionType =
  | {
      question: string;
    }
  | {
      questionComponent: React.ReactNode;
    };

export type WizardField<T extends wizardFieldsType = wizardFieldsType> = FormField &
  wizardQuestionType & {
    id: keyof T;
    title: string;
    enabled: Condition<T>;
    initialValue?: FieldValue | undefined;
    requireSubmit: true;
  };

export default function QuestionWrapper<T extends wizardFieldsType>({
  field,
  activeStep,
  previousStep,
  progress,
}: {
  field: WizardField<T>;
  activeStep: number;
  previousStep: () => void;
  progress: number;
}) {
  return (
    <>
      <Box
        sx={{
          pt: 7,
        }}
      >
        <Grid container spacing={5} justifyContent="center">
          <QuestionBody field={field} active />
        </Grid>
      </Box>
      <Footer
        maxWidth={false}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          padding: '10px 26px 26px 26px',
          position: 'fixed',
          bottom: 0,
          left: 220,
          width: 'auto',
          right: 0,
          pointerEvents: 'none',
        }}
      >
        <PrecursorFormProgress
          previousStep={previousStep}
          progress={progress}
          disablePrevious={false}
        />
      </Footer>
    </>
  );
}

export const QuestionBody = <T extends wizardFieldsType>({
  field,
  active,
  loading,
}: {
  field: WizardField<T>;
  active: boolean;
  loading?: boolean;
}) => {
  return (
    <Slide
      direction={active ? 'up' : 'down'}
      in={active}
      timeout={getEnvironment() === 'DEV' ? 0 : 500}
    >
      <Grid container justifyContent="flex-start" item md={8}>
        <Grid item xs={12} style={{marginBottom: '1rem'}}>
          <Typography variant="h1" color="textPrimary" style={{fontSize: '1.9em'}}>
            {field.title}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {'questionComponent' in field ? (
            field.questionComponent
          ) : (
            <Typography sx={classes.questionText}>{field.question}</Typography>
          )}
        </Grid>
        <Grid className="question" item minWidth={500} mt={4}>
          <AutoField loading={loading} field={field} needsValue />
        </Grid>
      </Grid>
    </Slide>
  );
};

export const PrecursorFormProgress = ({
  previousStep,
  progress,
  disablePrevious,
}: {
  previousStep: () => void;
  progress: number;
  disablePrevious?: boolean;
}) => {
  return (
    <>
      <ButtonGroup color="primary" aria-label="outlined primary button group">
        {!disablePrevious && (
          <Button
            style={{pointerEvents: 'auto'}}
            variant="contained"
            color="primary"
            onClick={previousStep}
            disabled={disablePrevious}
          >
            Back
          </Button>
        )}
      </ButtonGroup>
      <div style={classes.progressWrapper}>
        <Typography sx={classes.progressText}>{progress.toFixed(0)}% complete</Typography>
        <LinearProgress sx={classes.progress} variant="determinate" value={progress} />
      </div>
    </>
  );
};
